import { z } from "zod";
import { surfaceEnvelope } from "./shared";
import { NoteDocSchema } from "./note-doc";

export const ConversationListItemSchema = z.object({
  id: z.string(),
  conversation_title: z.string().nullish(),
  topic: z.string(),
  summary: z.string(),
  site: z.string(),
  estimated_tokens: z.number(),
  turn_count: z.number(),
  first_captured_at: z.string(),
  last_captured_at: z.string(),
  private: z.boolean(),
  project_name: z.string().nullish(),
}).passthrough();

export const ConversationProjectSchema = z.object({ id: z.string(), name: z.string() }).passthrough();

// Granola-only detail fields, returned by both detail routes since PR1; declared here in PR3 to close
// the contract drift (they previously survived only on `.passthrough()`). `.nullish()` — both routes
// return `null` for every non-Granola site.
export const MeetingParticipantSchema = z
  .object({ name: z.string(), email: z.string().nullish(), contribution: z.string().nullish() })
  .passthrough();
export type MeetingParticipant = z.infer<typeof MeetingParticipantSchema>;

export const SpeakerStatsSchema = z
  .object({
    n_speakers: z.number().nullish(),
    turn_count: z.number().nullish(),
    owner_share: z.number().nullish(),
    start_offset: z.number().nullish(),
    end_offset: z.number().nullish(),
    duration_seconds: z.number().nullish(),
  })
  .passthrough();
export type SpeakerStats = z.infer<typeof SpeakerStatsSchema>;

export const ConversationsSurfaceDataSchema = z.object({
  conversations: z.array(ConversationListItemSchema).default([]),
  total_count: z.number(),
  projects: z.array(ConversationProjectSchema).default([]),
}).passthrough();

export const ConversationDetailDataSchema = z.object({
  id: z.string(),
  conversation_title: z.string().nullish(),
  topic: z.string(),
  summary: z.string(),
  site: z.string(),
  compacted_text: z.string(),
  estimated_tokens: z.number(),
  turn_count: z.number(),
  entity_tags: z.array(z.string()).default([]),
  first_captured_at: z.string(),
  last_captured_at: z.string(),
  private: z.boolean(),
  project_name: z.string().nullish(),
  // Granola-only; returned by the route since PR1, declared here in PR3 to close the contract drift.
  meeting_participants: z.array(MeetingParticipantSchema).nullish(),
  speaker_stats: SpeakerStatsSchema.nullish(),
  // Conversation note redesign PR1 (Task 10): the assembled NoteDoc (viewer + query-engine
  // canonical read), the briefing state driving the detail state badge, and the
  // entry_kind discriminator. `.nullish()` — absent on legacy rows / not yet built by every writer.
  note_doc: NoteDocSchema.nullish(),
  briefing_category: z.string().nullish(),
  entry_kind: z.string().nullish(),
  // PR-3 "Open in source" (brief-phase1 spec §2): server-derived source-first deep link. Null for
  // ai-chat/claude-code notes (owner-only platform links), for a teammate's email note (per-mailbox
  // thread id), and whenever a viewer-scoped mirror join finds nothing — the note page itself
  // is the designed fallback.
  source_url: z.string().nullish(),
}).passthrough();

export const ConversationsSurfaceResponseSchema = surfaceEnvelope(ConversationsSurfaceDataSchema);
export const ConversationDetailResponseSchema = surfaceEnvelope(ConversationDetailDataSchema);

export type ConversationListItem = z.infer<typeof ConversationListItemSchema>;
export type ConversationProject = z.infer<typeof ConversationProjectSchema>;
export type ConversationsSurfaceData = z.infer<typeof ConversationsSurfaceDataSchema>;
export type ConversationDetailData = z.infer<typeof ConversationDetailDataSchema>;
export type ConversationsSurfaceResponse = z.infer<typeof ConversationsSurfaceResponseSchema>;
export type ConversationDetailResponse = z.infer<typeof ConversationDetailResponseSchema>;
